import { cn } from "@/lib/cn";

export type BadgeVariant =
  | "pink"
  | "violet"
  | "gold"
  | "neutral"
  | "success"
  | "danger";
export type BadgeSize = "sm" | "md";

const variants: Record<BadgeVariant, string> = {
  pink: "text-flamingo-lighter bg-flamingo/12 border-flamingo/20",
  violet: "text-violet-light bg-violet/12 border-violet/25",
  gold: "text-gold-light bg-gold/12 border-gold/25",
  neutral: "text-white/70 bg-white/6 border-white/10",
  success: "text-success-light bg-success/12 border-success/25",
  danger: "text-danger-light bg-danger/12 border-danger/25",
};

const sizes: Record<BadgeSize, string> = {
  sm: "h-5 gap-1 px-2 text-2xs",
  md: "h-6 gap-1.5 px-2.5 text-xs",
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: React.ReactNode;
  /** Show a small pulsing dot (e.g. online / live). */
  dot?: boolean;
}

export function Badge({
  variant = "neutral",
  size = "md",
  icon,
  dot,
  className,
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border font-semibold tracking-wide uppercase",
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {dot && (
        <span className="relative flex size-1.5">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-current opacity-60" />
          <span className="relative inline-flex size-1.5 rounded-full bg-current" />
        </span>
      )}
      {icon}
      {children}
    </span>
  );
}
